/* Promotion links: ?sort_variant=VARIANT_ID,VARIANT_ID
 * Uses the existing theme cards and globally reorders before slicing each page.
 * No Admin API credentials, cart changes, or persistent collection changes.
 */
(() => {
  'use strict';

  window.theme = window.theme || {};
  if (window.theme.collectionVariantSort) return;

  const indexCache = new Map();
  const activeRuns = new WeakMap();
  const cardSelector = '[data-collection-product-id]';

  function parseVariantIds(value) {
    return [...new Set((value || '').split(/[,，]/).map((id) => id.trim())
      .filter((id) => /^[1-9]\d*$/.test(id)))];
  }

  function buildOrder(products, variantIds) {
    const variants = new Map();
    products.forEach((product) => {
      if (!product.hidden) product.variants.forEach((id) => variants.set(String(id), product.id));
    });
    const pinnedIds = new Set();
    variantIds.forEach((id) => {
      const productId = variants.get(id);
      if (productId) pinnedIds.add(productId);
    });
    const byId = new Map(products.map((product) => [product.id, product]));
    return {
      pinnedIds,
      products: [...pinnedIds].map((id) => byId.get(id))
        .concat(products.filter((product) => !pinnedIds.has(product.id))),
    };
  }

  async function mapLimited(items, callback, concurrency = 3) {
    const results = new Array(items.length);
    let next = 0;
    await Promise.all(Array.from({ length: Math.min(concurrency, items.length) }, async () => {
      while (next < items.length) {
        const index = next++;
        results[index] = await callback(items[index]);
      }
    }));
    return results;
  }

  function baseUrl(href) {
    const url = new URL(href);
    ['page', 'sort_variant', 'section_id', 'sections'].forEach((key) => url.searchParams.delete(key));
    url.hash = '';
    return url;
  }

  async function fetchDocument(url, signal) {
    const controller = new AbortController();
    const abort = () => controller.abort();
    if (signal.aborted) controller.abort();
    else signal.addEventListener('abort', abort, { once: true });
    const timeout = setTimeout(abort, 15000);
    try {
      const response = await fetch(url.href, { signal: controller.signal, credentials: 'same-origin' });
      if (!response.ok) throw new Error(`Collection request failed (${response.status})`);
      return new DOMParser().parseFromString(await response.text(), 'text/html');
    } finally {
      clearTimeout(timeout);
      signal.removeEventListener('abort', abort);
    }
  }

  async function loadIndex(url, signal, variantIds, requiredCount) {
    const key = url.href;
    let cached = indexCache.get(key);
    if (!cached || Date.now() - cached.time >= 60000) {
      if (indexCache.size >= 4) indexCache.delete(indexCache.keys().next().value);
      cached = { pages: new Map(), time: Date.now() };
      indexCache.set(key, cached);
    }

    async function loadPage(page) {
      if (signal.aborted) throw new DOMException('Aborted', 'AbortError');
      if (cached.pages.has(page)) return cached.pages.get(page);
      const request = new URL(url);
      request.searchParams.set('section_id', 'collection-sort-index');
      request.searchParams.set('page', String(page));
      const boot = window.msblueVariantSortBoot;
      let doc;
      if (page === 1 && boot?.baseHref === key && boot.firstIndex) {
        const earlyRequest = boot.firstIndex;
        boot.firstIndex = null;
        const abortEarly = () => boot.controller.abort();
        signal.addEventListener('abort', abortEarly, { once: true });
        let html;
        try { html = await earlyRequest; }
        finally { signal.removeEventListener('abort', abortEarly); }
        if (signal.aborted) throw new DOMException('Aborted', 'AbortError');
        if (!html) throw new Error('Collection index prefetch failed');
        doc = new DOMParser().parseFromString(html, 'text/html');
      } else {
        doc = await fetchDocument(request, signal);
      }
      const script = doc.querySelector('[data-collection-sort-index]');
      if (!script) throw new Error('Collection sort index is missing');
      const data = JSON.parse(script.textContent);
      if (data.page !== page || !Array.isArray(data.products) || !Number.isInteger(data.pages)) {
        throw new Error('Invalid collection sort index');
      }
      cached.pages.set(page, data);
      return data;
    }

    const first = await loadPage(1);
    if (first.count > 25000) throw new Error('Collection exceeds Shopify pagination limit');
    const products = [...first.products];
    const knownVariants = new Set(products.flatMap((product) => product.variants));
    const pinsFound = () => variantIds.every((id) => knownVariants.has(id));
    const enough = () => products.length >= Math.min(requiredCount, first.count) && pinsFound();
    // A contiguous prefix is sufficient once all pins and the requested page fit.
    // Products after this prefix cannot affect that page's stable ordering.
    for (let nextPage = 2; nextPage <= first.pages && !enough(); nextPage += 3) {
      const lastNeededPage = pinsFound() ? Math.min(first.pages, Math.ceil(requiredCount / 250)) : first.pages;
      const pages = Array.from({ length: Math.min(3, lastNeededPage - nextPage + 1) }, (_, i) => nextPage + i);
      const rest = await mapLimited(pages, loadPage);
      rest.forEach((data) => {
        if (data.count !== first.count || data.pages !== first.pages) throw new Error('Collection changed while loading');
        products.push(...data.products);
        data.products.forEach((product) => product.variants.forEach((id) => knownVariants.add(id)));
      });
    }
    if ((!enough() && products.length !== first.count) || new Set(products.map((product) => product.id)).size !== products.length) {
      throw new Error('Collection changed while loading the sort index');
    }
    return products;
  }

  function reveal(outer) {
    outer?.removeAttribute('aria-busy');
    outer?.setAttribute('data-variant-sort-ready', '');
  }

  function cancel(container) {
    const run = activeRuns.get(container);
    if (!run) return;
    run.controller.abort();
    clearTimeout(run.timer);
    reveal(run.outer);
    activeRuns.delete(container);
    const boot = window.msblueVariantSortBoot;
    if (boot?.href === run.href) {
      boot.finished = true;
      clearTimeout(boot.timer);
    }
  }

  function preserveLinks(container, href) {
    const value = new URL(href).searchParams.get('sort_variant');
    if (!value) return;
    container.querySelectorAll('.pagination-custom a, [data-filter-update-url]').forEach((link) => {
      const url = new URL(link.getAttribute('href'), href);
      if (url.origin !== window.location.origin || url.pathname !== new URL(href).pathname) return;
      url.searchParams.set('sort_variant', value);
      link.setAttribute('href', url.pathname + url.search + url.hash);
    });
  }

  async function sort(container) {
    cancel(container);
    const href = window.location.href;
    const boot = window.msblueVariantSortBoot;
    const ids = parseVariantIds(new URL(href).searchParams.get('sort_variant'));
    const outer = container.querySelector('[data-products-grid]');
    if (!ids.length || (boot?.expired && boot.href === href)) { reveal(outer); return; }
    const grid = outer?.querySelector('#CollectionLoop');
    const pageSize = Number(container.dataset.variantSortPageSize);
    const page = Number(outer?.dataset.variantSortPage);
    if (!grid || !Number.isInteger(pageSize) || pageSize < 1 || !Number.isInteger(page) || page < 1) { reveal(outer); return; }

    preserveLinks(container, href);
    const run = { controller: new AbortController(), outer, href };
    activeRuns.set(container, run);
    document.documentElement.classList.add('variant-sort-active');
    outer.removeAttribute('data-variant-sort-ready');
    outer.setAttribute('aria-busy', 'true');
    const budget = boot && boot.href === href && !boot.finished ? boot.deadline - Date.now() : 8000;
    if (boot?.href === href) clearTimeout(boot.timer);
    run.timer = setTimeout(() => {
      if (activeRuns.get(container) === run) cancel(container);
    }, Math.max(0, budget));

    try {
      const url = baseUrl(href);
      const localProducts = Array.from(grid.querySelectorAll(cardSelector)).map((card) => ({
        id: card.dataset.collectionProductId,
        variants: (card.dataset.collectionVariantIds || '').split(','), hidden: false,
      }));
      const localVariants = new Set(localProducts.flatMap((product) => product.variants));
      // On page one, moving pins already present cannot change page membership.
      // Complete synchronously before the next paint, even if index requests fail.
      const localOnly = page === 1 && ids.every((id) => localVariants.has(id));
      const products = localOnly ? localProducts : await loadIndex(url, run.controller.signal, ids, page * pageSize);
      if (localOnly && boot?.href === href) {
        boot.controller.abort();
        boot.firstIndex = null;
      }
      const ordered = buildOrder(products, ids);
      if (!ordered.pinnedIds.size) return;
      const wanted = ordered.products.slice((page - 1) * pageSize, page * pageSize)
        .filter((product) => !product.hidden);
      const originalPositions = new Map(products.map((product, index) => [product.id, index]));
      const cards = new Map(Array.from(grid.querySelectorAll(cardSelector))
        .map((card) => [card.dataset.collectionProductId, card]));
      const missingPages = [...new Set(wanted.filter((product) => !cards.has(product.id))
        .map((product) => Math.floor(originalPositions.get(product.id) / pageSize) + 1))];

      const docs = missingPages.length ? await mapLimited(missingPages, async (sourcePage) => {
        const request = new URL(url);
        request.searchParams.set('section_id', container.dataset.sectionId);
        request.searchParams.set('page', String(sourcePage));
        return fetchDocument(request, run.controller.signal);
      }) : [];
      docs.forEach((doc) => {
        const source = doc.getElementById(container.id);
        source?.querySelectorAll(`#CollectionLoop ${cardSelector}`).forEach((card) => {
          cards.set(card.dataset.collectionProductId, card);
        });
      });
      // Commit only when every card is available and this is still the latest request.
      if (activeRuns.get(container) !== run || run.controller.signal.aborted || window.location.href !== href) return;
      if (wanted.some((product) => !cards.has(product.id))) throw new Error('A requested product card is missing');

      const orderedCards = wanted.map((product, index) => {
        const card = cards.get(product.id);
        const cover = card.querySelector('[data-product-image-default] img');
        if (cover && index < 10) cover.loading = 'eager';
        return card;
      });
      // Keep editorial tiles in their existing slots, after the promotion's leading cards.
      const tiles = Array.from(grid.children).map((node, index) => ({ node, index }))
        .filter(({ node }) => !node.matches(cardSelector));
      const leadingPins = wanted.filter((product) => ordered.pinnedIds.has(product.id)).length;
      let previousTile = -1;
      tiles.forEach(({ node, index }) => {
        const position = Math.min(orderedCards.length, Math.max(index, leadingPins, previousTile + 1));
        orderedCards.splice(position, 0, node);
        previousTile = position;
      });
      grid.replaceChildren(...orderedCards);
      // Start decoding only the first visible row; don't delay the whole grid for images.
      grid.querySelectorAll('[data-product-image-default] img').forEach((img, index) => {
        if (index < 5 && img.decode) img.decode().catch(() => {});
      });
      grid.querySelectorAll('img.is-loading').forEach((img) => {
        if (img.complete && img.naturalWidth > 0) {
          img.classList.remove('is-loading');
          img.closest('.lazy-image')?.classList.remove('is-loading');
        }
      });
      grid.querySelectorAll('[data-aos]').forEach((node) => {
        node.setAttribute('data-aos-delay', '0');
        node.classList.add('aos-animate');
      });
      preserveLinks(container, href);
      outer.dataset.variantSortResult = localOnly ? 'local' : 'remote';
    } catch (error) {
      if (activeRuns.get(container) === run && !run.controller.signal.aborted) {
        // Fail open: keep the original usable collection if the network/API fails.
        console.warn('[Collection variant sort]', error);
        indexCache.delete(baseUrl(href).href);
        boot?.clearCache?.();
        run.controller.abort();
      }
    } finally {
      clearTimeout(run.timer);
      if (activeRuns.get(container) === run) {
        reveal(outer);
        activeRuns.delete(container);
        if (boot?.href === href) {
          boot.finished = true;
          clearTimeout(boot.timer);
        }
      }
    }
  }

  window.theme.collectionVariantSort = { version: 3, sort, cancel, parseVariantIds, buildOrder };
  const initialized = new WeakSet();
  const start = (root, mountedOnly = false) => root.querySelectorAll('[data-variant-sort-page-size]').forEach((container) => {
    if (initialized.has(container) || (mountedOnly && !container.hasAttribute('data-variant-sort-mounted'))) return;
    initialized.add(container);
    sort(container);
  });
  start(document, document.readyState === 'loading');
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => start(document), { once: true });
  document.addEventListener('msblue:collection:ready', () => start(document, true));
  document.addEventListener('shopify:section:load', (event) => start(event.target));
  window.addEventListener('msblue:variant-sort:expired', () => {
    document.querySelectorAll('[data-variant-sort-page-size]').forEach(cancel);
  });
})();
